import { Controller } from '@hotwired/stimulus'
import { createApp } from 'vue'
import TiptapEditor from '~/javascript/components/admin/blog/TiptapEditor.vue'

/**
 * Blog Editor Controller
 *
 * Mounts a TipTap rich text editor Vue component and syncs content
 * with a hidden form field for submission.
 *
 * Usage:
 *   <div data-controller="admin--blog-editor"
 *        data-admin--blog-editor-content-value='{"type":"doc","content":[]}'
 *        data-admin--blog-editor-field-id-value="blog-post-body">
 *   </div>
 *   <input type="hidden" id="blog-post-body" name="blog_post[body]">
 */
export default class extends Controller {
  static values = {
    content: { type: String, default: '' },
    fieldId: { type: String, default: 'blog-post-body' },
    placeholder: { type: String, default: 'Start writing your blog post...' }
  }

  connect() {
    this.mountVueComponent()
  }

  disconnect() {
    this.unmountVueComponent()
  }

  mountVueComponent() {
    const fieldId = this.fieldIdValue
    const initialContent = this.contentValue
    const placeholder = this.placeholderValue

    // Create Vue app with TiptapEditor directly
    this.vueApp = createApp(TiptapEditor, {
      modelValue: initialContent,
      placeholder,
      'onUpdate:modelValue': (value) => {
        const hiddenField = document.getElementById(fieldId)
        if (hiddenField) {
          hiddenField.value = value
        }
      }
    })

    this.vueApp.mount(this.element)

    // Set initial value in hidden field if we have content
    if (initialContent) {
      const hiddenField = document.getElementById(fieldId)
      if (hiddenField) {
        hiddenField.value = initialContent
      }
    }
  }

  unmountVueComponent() {
    if (this.vueApp) {
      this.vueApp.unmount()
      this.vueApp = null
    }
  }

  // Called when content value changes externally
  contentValueChanged() {
    if (this.vueApp) {
      this.unmountVueComponent()
      this.mountVueComponent()
    }
  }
}
